/**
 * MKS-163 follow-up — standalone, always-visible priority filter pills for
 * the KanbanBoardPage toolbar.
 *
 * PURELY presentational (mirrors BoardFilterBar/BoardListSwitcher): no data
 * hooks, no auth hooks. The page owns `filters.priority` (BoardFilterState,
 * see utils/boardFilters.ts) and passes it down as `value` / `onChange`,
 * exactly like every other board-filter control.
 *
 * History: this control briefly lived as a row INSIDE the Filters popover
 * (BoardFilterBar). Moved out per user feedback from a screenshot review —
 * "one-click priority filtering without opening the panel." The underlying
 * `BoardFilterState.priority` slot, `taskMatchesBoardFilters` predicate, and
 * `filterBoardColumns` application are UNCHANGED; only the editing control's
 * mounting point moved. `BoardFilterBar`'s "Clear filters" button still
 * resets `priority` to `[]` (it always has — it resets the whole state
 * object), so clearing from the panel and clearing by deselecting every pill
 * here both work.
 *
 * Renders for BOTH audiences, same as `BoardFilterBar` (MKS-134 superseded
 * the original "internal only" gate) — the parent mounts this component with
 * the identical `data != null && data.columns.length > 0` gate as
 * `BoardFilterBar`, so the two controls appear/disappear together.
 *
 * Compact by design (badge + short label) — the toolbar has no existing
 * responsive/overflow pattern (no breakpoint classes, no truncation beyond
 * the project-name `<h1>`) to mirror, so this control does not introduce one
 * either; see the 260911-o8n-SUMMARY.md "Scope Extension" notes for the
 * explicit decision.
 */
import { MtPriorityBadge, priorityConfig } from '../mytasks/MtPriorityBadge';
import type { TaskPriority } from '../../services/api/board';

// MKS-163 (HR-2) — mirrors TASK_PRIORITIES in
// backend/src/api/schemas/taskMutation.ts / TaskPriorityPicker.tsx. A fixed
// system enum, not board-specific data, so it is hardcoded here rather than
// threaded in as a prop (unlike assignees/statuses/milestones, which vary
// per board and are collected from board data).
const TASK_PRIORITIES: TaskPriority[] = ['urgent', 'high', 'normal', 'low'];

export interface PriorityFilterToggleProps {
  value: TaskPriority[];
  onChange: (next: TaskPriority[]) => void;
}

export function PriorityFilterToggle({ value, onChange }: PriorityFilterToggleProps) {
  return (
    <div
      role="group"
      aria-label="Filter board by priority"
      data-testid="priority-filter-toggle"
      className="inline-flex items-center gap-1 shrink-0"
    >
      {TASK_PRIORITIES.map((p) => {
        const selected = value.includes(p);
        const cfg = priorityConfig(p);
        return (
          <button
            key={p}
            type="button"
            data-testid={`priority-filter-toggle-${p}`}
            aria-pressed={selected}
            title={`Filter by ${cfg.label} priority`}
            onClick={() =>
              onChange(selected ? value.filter((x) => x !== p) : [...value, p])
            }
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[12px] font-medium border transition-colors ${
              selected
                ? 'border-ms-orange bg-ms-orange/10 text-ms-orange-dark font-semibold'
                : 'border-ms-border bg-ms-surface text-ms-text-secondary hover:bg-ms-canvas'
            }`}
          >
            <MtPriorityBadge priority={p} withLabel={false} />
            {cfg.label}
          </button>
        );
      })}
    </div>
  );
}
